<template>
  <div id="map" class="fit-parent-transit-false">
    <l-map
      ref="map"
      class="map-style"
      :zoom="zoom"
      :center="center"
      :bounds="mapBounds">
      <l-tile-layer :url="OSMHOST" />
      <l-control>
        <Speedometer
          :value="driver && driver.speed ? driver.speed : 0"
          :class="isMobile ? 'speedometer-map-mobile ': 'speedometer-map'" />
      </l-control>
      <l-marker
        v-for="(_marker, i) in markerData"
        :key="i"
        :lat-lng.sync="_marker.position">
        <l-icon>
          <img :src="_marker.iconUrl" :class="_marker.className">
        </l-icon>
      </l-marker>
    </l-map>
  </div>
</template>
<script>
import Const from '@/utils/const'
import turf from '@turf/bearing'
import Speedometer from '@/components/Speedometer'
import { isMobile } from '@/utils/functions'

import L from 'leaflet'
import { LMap, LTileLayer, LControl, LMarker, LIcon } from 'vue2-leaflet'

export default {
  name: 'Map',
  components: {
    Speedometer,
    LMap,
    LTileLayer,
    LControl,
    LMarker,
    LIcon
  },
  props: {
    driver: {
      type: Object,
      required: false,
      default: () => null
    },
    branch: {
      type: Object,
      required: false,
      default: () => null
    },
    delivery: {
      type: Object,
      required: false,
      default: () => null
    },
    isOrderOpen: {
      type: Boolean,
      required: false,
      default: () => null
    },
    disconnectedRouteCoordinates: {
      type: Array,
      required: false,
      default: () => null
    },
    isDisconected: {
      type: Boolean,
      required: false,
      default: () => null
    },
    animationDisconnected: {
      type: Boolean,
      required: false,
      default: () => null
    },
    bearingLocation: {
      type: Array,
      required: false,
      default: () => null
    }
  },
  data() {
    return {
      lastCamera: null,
      const: {
        type: Const.map.type
      },
      access_token: process.env.VUE_APP_MAP_ACCESS_TOKEN,
      map: null,
      optimize: true,
      markers: {
        driver: null,
        branch: null,
        delivery: null
      },
      lastDriver: null,
      colorMode: null,
      isEnable: false,
      value: Boolean,
      camera: Const.map.type.ROUTE,
      selection: Const.map.type.ROUTE,
      connectionSocket: true,
      animationDriverSocket: true,
      bearingLastLocation: null,
      zoom: 15,
      OSMHOST: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      markerData: []
    }
  },
  computed: {
    getCameraIcon() {
      return this.camera === this.const.type.ROUTE ? '/img/ic_compass_nav.png' : '/img/ic_map.png'
    },
    isMobile: function() {
      return isMobile()
    },
    center() {
      return L.latLng(
        this.branch.lat,
        this.branch.lng
      )
    },
    mapBounds() {
      const _markers = this.markerData
      let bounds = null
      if (Array.isArray(_markers) && _markers.length > 0) {
        bounds = L.latLngBounds(_markers.map(m => m.position)).pad(0.5, 0.2)
      }
      return bounds
    }
  },
  watch: {
    driver(current, old) {
      this.updateMarkerPosition(current, Const.map.marker.DRIVER)
    },
    branch(current, old) {
      this.updateMarkerPosition(current, Const.map.marker.BRANCH)
    },
    delivery(current, old) {
      this.updateMarkerPosition(current, Const.map.marker.DELIVERY)
    },
    disconnectedRouteCoordinates(current, old) {
      if (this.lastCamera === Const.map.type.ROUTE && this.camera === Const.map.type.NAVIGATION) {
        this.loadSelection(Const.map.type.ROUTE)
      }
      if (Array.isArray(current) && current.length > 0) {
        this.drawLineOnMap(current)
        this.followRoute(current)
      } else {
        this.removeLineFromMap()
      }
    }
  },
  mounted() {
    this.createMap()
  },
  methods: {
    createMap() {
      if (this.branch) {
        this.addMarker(this.branch, Const.map.marker.BRANCH)
      }
      if (this.delivery) {
        this.addMarker(this.delivery, Const.map.marker.DELIVERY)
      }
      if (this.driver) {
        this.addMarker(this.driver, Const.map.marker.DRIVER)
      }
    },
    addMarker(data, type) {
      const _markerInfo = {}
      if (data && data.lat && data.lng) {
        _markerInfo.position = { lat: data.lat, lng: data.lng }
      }

      if (_markerInfo.position) {
        _markerInfo.className = `marker-${type}`
        _markerInfo.iconUrl = Const.map.icon[type]
        _markerInfo.type = type

        const map = this.$refs.map.mapObject
        this.markerData.push(_markerInfo)
        const bounds = L.latLngBounds(this.markerData.map(m => m.position)).pad(0.5, 0.2)
        map.fitBounds(bounds)
      }
    },
    updateMarkerPosition(data, type) {
      const _markerIndex = this.markerData.findIndex(_marker => _marker.type === type)
      if (_markerIndex === -1) {
        this.addMarker(data, type)
      } else if (data && data.lat && data.lng) {
        this.markerData[_markerIndex].position = { lat: data.lat, lng: data.lng }
        const map = this.$refs.map.mapObject
        const bounds = L.latLngBounds(this.markerData.map(m => m.position)).pad(0.5, 0.2)
        map.fitBounds(bounds)
      }
    },
    updateMarkerLocation(start, end) {
      if (start && start !== end) {
        const n = 100 // the number of coordinates you want
        const coordinates = []
        for (var i = n - 1; i > 0; i--) {
          coordinates.push([
            start.lng * i / n + end.lng * (n - i) / n,
            start.lat * i / n + end.lat * (n - i) / n
          ])
        }
        coordinates.forEach(element => {
          setTimeout(() => {
            // Update driver
            this.markers.driver.setLngLat(element)
            // Backup last data
            if (this.markers.driver) {
              this.lastDriver = this.markers.driver
              this.markers.driver.setRotation(
                this.getBearing(start, end)
              )
            }
          }, 500)
        })
      } else {
        this.markers.driver.setLngLat([
          end.lng,
          end.lat
        ])
      }
    },
    fitBounds(data) {
      if (this.map) {
        const bounds = []
        for (const value of Object.values(this.markers)) {
          if (value && value._lngLat && value._lngLat.lng && value._lngLat.lat) {
            bounds.push([
              value._lngLat.lng,
              value._lngLat.lat
            ])
          }
        }
        if (bounds.length > 0) {
          if (bounds.length === 1) {
            const animationOptions = {
              center: bounds[0],
              duration: 2000,
              easing(t) {
                return 1 - Math.pow(1 - t, 5)
              },
              offset: [0, 0],
              animate: true,
              essential: true, // animation will happen even if user has `prefers-reduced-motion` setting on
              pitch: 0
            }
            if (data.bearing) {
              animationOptions.bearing = data.bearing
            }
            this.map.easeTo(animationOptions)
          }
        }
      }
    },
    loadNav(data) {
      const animationOptions = {
        center: data.location,
        duration: 4000,
        easing(t) {
          return 1 - Math.pow(1 - t, 5)
        },
        offset: [0, 0],
        animate: true,
        essential: true, // animation will happen even if user has `prefers-reduced-motion` setting on
        zoom: 15,
        pitch: 60
      }
      if (data.bearing) {
        animationOptions.bearing = data.bearing
      }
      this.map.easeTo(animationOptions)
    },
    toggleCamera() {
      this.loadSelection(
        this.camera === Const.map.type.ROUTE ? Const.map.type.NAVIGATION : Const.map.type.ROUTE
      )
    },
    loadSelection(option) {
      // Update camera value
      this.lastCamera = this.camera
      this.camera = option
      if (this.camera === Const.map.type.NAVIGATION && this.markers[Const.map.marker.DRIVER]) {
        this.markers[Const.map.marker.DRIVER].remove()
        delete this.markers.driver
      }
      if (this.lastDriver) {
        this.addMarker(
          {
            type: Const.map.marker.DRIVER,
            bearing: this.lastDriver._rotation,
            location: [
              this.lastDriver._lngLat.lng,
              this.lastDriver._lngLat.lat
            ]
          },
          Const.map.marker.DRIVER
        )
      }
      if (this.map) {
        if (this.camera === Const.map.type.ROUTE) {
          this.map.dragPan.enable()
        } else {
          this.map.dragPan.disable()
        }
      }
    },
    motoMap() {
      this.value = !this.isEnable
      this.isEnable = !this.isEnable
    },
    drawLineOnMap(routeCoordinates) {
      this.map.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: routeCoordinates
          }
        }
      })
      this.map.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#888',
          'line-width': 8
        }
      })
    },
    removeLineFromMap() {
      this.map.removeLayer('route')
      this.map.removeSource('route')
    },
    followRoute(routeCoordinates, time) {
      const coordinateDuration = time || 6000 / routeCoordinates.length
      if (this.camera !== Const.map.type.NAVIGATION) {
        this.loadSelection(Const.map.type.NAVIGATION)
      }
      routeCoordinates.forEach((coordinate, i) => {
        // fly to
        setTimeout(() => {
          if (routeCoordinates[i] === routeCoordinates[0]) {
            this.bearingLastLocation = this.bearingLocation
          }
          this.followCoordinates(coordinate, coordinateDuration, this.bearingLastLocation == null ? 0 : this.getBearing(this.bearingLastLocation, routeCoordinates[i]))
          this.bearingLastLocation = routeCoordinates[i]
        }, i * coordinateDuration)
      })
    },
    followCoordinates(coordinates, duration, bearingData) {
      // Backup last data
      if (this.lastDriver !== null) {
        this.lastDriver._lngLat.lng = coordinates[0]
        this.lastDriver._lngLat.lat = coordinates[1]
        if (bearingData >= 0) {
          this.lastDriver.setRotation(
            bearingData
          )
        }
      }
      this.map.flyTo({
        center: coordinates,
        speed: 0.4,
        duration,
        bearing: bearingData,
        curve: 1,
        easing(t) {
          return 1 - Math.pow(1 - t, 5)
        }
      })
    },
    getBearing(coordinate1, coordinate2) {
      const point1 = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [coordinate1[0], coordinate1[1]]
        }
      }
      const point2 = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [coordinate2[0], coordinate2[1]]
        }
      }
      return turf(point1, point2)
    }
  }
}
</script>
<style>
.leaflet-top {
  top: 132px !important;
}
.button-map-2d-display{
  display:none;
}
.button-map-3d-display{
  display:none;
}
.button-map-2d {
  z-index: 2;
  top: 6%;
  right: 18px;
  height: 35px;
  width: 35px;
  position: absolute;
  margin-top: 15px
}
#fit {
  padding-top: 2%;
  padding-bottom: 2%;
}
.speedometer-map {
  z-index: 3;
  top: 16.3%;
  margin-left: calc(50% - 300px);
  position: absolute;
}
.speedometer-map-mobile {
  z-index: 3;
  top: 2%;
  margin-left: calc(50% - 250px);
  position: absolute;
}
.button-connection-map-2d {
  z-index: 2;
  top: 2%;
  right: 7%;
  height: 35px;
  width: 35px;
  position: absolute;
}

.button-map-3d {
  z-index: 2;
  top: 42%;
  right: 7%;
  height: 35px;
  width: 35px;
  position: absolute;
}
.driver-nav{
  z-index: 1;
  height: 35px;
  width: 35px;
  margin: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

@media only screen and (min-width: 540px) and (max-width: 767px) {
  .button-map-2d {
  z-index: 2;
  top: 19%;
  right: 7%;
  height: 50px;
  width: 50px;
  position: absolute;
}

.button-map-3d {
  z-index: 2;
  top: 42%;
  right: 7%;
  height: 50px;
  width: 50px;
  position: absolute;
}

}
@media only screen and (min-width: 768px) and (max-width: 1023px) {
  .button-map-2d {
  z-index: 2;
  top: 19%;
  right: 7%;
  height: 50px;
  width: 50px;
  position: absolute;
}
.button-map-3d {
  z-index: 2;
  top: 42%;
  right: 7%;
  height: 50px;
  width: 50px;
  position: absolute;
}
}

@media only screen and (min-width: 1024px) {
  .button-map-2d {
  z-index: 2;
  top: 18%!important;
  right: 3%;
  height: 50px;
  width: 50px;
  position: absolute;
  margin-top: 10px;
}
.button-map-3d {
  z-index: 2;
  top: 40%;
  right: 7%;
  height: 50px;
  width: 50px;
  bottom: 0;
  position: absolute;
}
}

.marker-driver {
  border: none;
  cursor: pointer;
  height: 33.8px;
  width: 18px;
  background-color: rgba(0, 0, 0, 0);
  background-size: contain;
}

.marker-branch {
  border: none;
  cursor: pointer;
  height: 40px;
  width: 44px;
  background-color: rgba(0, 0, 0, 0);
  background-size: contain;
}

.marker-delivery {
  border: none;
  cursor: pointer;
  height: 41px;
  width: 36px;
  background-color: rgba(0, 0, 0, 0);
  background-size: contain;
}

.map-style {
  width:100%;
  height: 100%
}
</style>
